<script setup lang="ts">
import { ElSelect, ElOption } from "element-plus";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGetCompanySettings, type CompanySettings } from "@/api/companies";
import { outTimes } from "@/utils/data";
import CheckboxSetting from "@/components/common/CheckboxSetting/CheckboxSetting.vue";
import CoreLink from "@/components/common/CoreLink/CoreLink.vue";
import CoreSaveConfirm from "@/components/common/CoreSaveConfirm/CoreSaveConfirm.vue";
import SettingsTwoColumn from "@/components/layout/settings/SettingsTwoColumn.vue";
import { useCompanySettings } from "@/composables/company";
import { useSubscription } from "@/composables/subscription";
import { useDevices } from "@/composables/devices";
import { PLAN_FEATURE } from "@/stores/constants";

const { t, locale } = useI18n();

const { isFeatureBundled, isLoading } = useSubscription();
const { devicesAtSelectedGroup } = useDevices();
const getCompanySettings = useGetCompanySettings();
const { updateSetting, settingsSaveStatus } = useCompanySettings();

const settings = computed<Partial<CompanySettings>>(
  () => getCompanySettings.data.value ?? {}
);

const autoLogoutHour = ref<string>(
  settings.value.auto_logout_hour_employee ?? "12am"
);

const listOutTimes = ref(outTimes());

watch(
  () => settings.value.auto_logout_hour_employee,
  (value) => {
    autoLogoutHour.value = value ?? "12am";
  }
);

const hasPhotoVerification = isFeatureBundled(
  PLAN_FEATURE.EMPLOYEE_VERIFICATION_PHOTOS
);

const employeeInOutDisabledForIpads = computed(
  () =>
    devicesAtSelectedGroup.value?.every(
      (device) => !device.location.is_employee_in_out_enabled
    )
);

const autoLogoutDisabled = computed(
  () => employeeInOutDisabledForIpads.value && !settings.value.mobile?.movements
);

watch(locale, () => {
  listOutTimes.value = outTimes();
});
</script>
<template>
  <SettingsTwoColumn>
    <template #col-one>
      <h3 class="section-title">{{ t("employeeSettings.general.title") }}</h3>
      <CheckboxSetting
        :value="
          settings.employee_remote &&
          isFeatureBundled(PLAN_FEATURE.WORKING_REMOTE)
        "
        :label="t('employeeSettings.general.workingRemote')"
        :loading="getCompanySettings.isLoading.value || isLoading"
        :disabled="!isFeatureBundled(PLAN_FEATURE.WORKING_REMOTE)"
        @change="updateSetting('employee_remote', Boolean($event))"
      >
        <template
          v-if="!isFeatureBundled(PLAN_FEATURE.WORKING_REMOTE)"
          #disabled-suffix
        >
          <CoreLink
            type="primary"
            :underline="false"
            :route="{
              name: 'change-plan',
              query: { feature: PLAN_FEATURE.WORKING_REMOTE },
            }"
          >
            {{ t("employeeSettings.general.upgrade") }}
          </CoreLink>
        </template>
        <i18n-t
          v-if="isFeatureBundled(PLAN_FEATURE.WORKING_REMOTE)"
          keypath="employeeSettings.general.workingRemoteDesc"
        >
          <template #link>
            <CoreLink
              target="_blank"
              rel="noopener noreferrer"
              :href="t('urls.support.workingRemote')"
              type="primary"
              :underline="false"
            >
              {{ t("employeeSettings.general.learnMoreLinkText") }}
            </CoreLink>
          </template>
        </i18n-t>
        <template #suffix>
          <CoreSaveConfirm :status="settingsSaveStatus.employee_remote" />
        </template>
      </CheckboxSetting>
      <CheckboxSetting
        :value="settings.emp_autologout"
        :loading="getCompanySettings.isLoading.value"
        :label="t('employeeSettings.general.autoSignOut')"
        :disabled="autoLogoutDisabled"
        :disabled-message="
          t('employeeSettings.tooltips.enablePocketOrIpadEmployeeInOut')
        "
        @change="updateSetting('emp_autologout', Boolean($event))"
      >
        <el-select
          v-if="settings.emp_autologout"
          v-model="autoLogoutHour"
          :disabled="autoLogoutDisabled"
          @change="updateSetting('auto_logout_hour_employee', $event)"
        >
          <el-option
            v-for="{ value, label } in listOutTimes"
            :key="value"
            :label="label"
            :value="value"
          />
        </el-select>
        <CoreSaveConfirm
          class="logout-selector-save-confirm"
          :status="settingsSaveStatus.auto_logout_hour_employee"
        />
        <template #suffix>
          <CoreSaveConfirm :status="settingsSaveStatus.emp_autologout" />
        </template>
      </CheckboxSetting>
      <CheckboxSetting
        :value="settings.profile_capture_e"
        :loading="getCompanySettings.isLoading.value"
        :label="t('employeeSettings.general.updateProfilePhoto')"
        @change="updateSetting('profile_capture_e', Boolean($event))"
      >
        <i18n-t keypath="employeeSettings.general.updateProfilePhotoDesc">
          <template #link>
            <CoreLink
              target="_blank"
              rel="noopener noreferrer"
              :href="t('urls.support.updatingEmployeePhoto')"
              type="primary"
              :underline="false"
            >
              {{ t("employeeSettings.general.learnMoreLinkText") }}
            </CoreLink>
          </template>
        </i18n-t>
        <template #suffix>
          <CoreSaveConfirm :status="settingsSaveStatus.profile_capture_e" />
        </template>
      </CheckboxSetting>
    </template>
    <template #col-two>
      <h3 class="section-title">
        {{ t("employeeSettings.general.tabletTitle") }}
      </h3>
      <CheckboxSetting
        :value="settings.photo_capture_e && hasPhotoVerification"
        :disabled="!hasPhotoVerification"
        :disabled-message="
          hasPhotoVerification
            ? t('employeeSettings.tooltips.enableIpadEmployeeInOut')
            : undefined
        "
        :loading="getCompanySettings.isLoading.value"
        :label="t('employeeSettings.general.verificationPhoto')"
        @change="updateSetting('photo_capture_e', Boolean($event))"
      >
        <template
          v-if="!hasPhotoVerification"
          #disabled-suffix
        >
          <CoreLink
            type="primary"
            :underline="false"
            :route="{
              name: 'change-plan',
              query: { feature: PLAN_FEATURE.EMPLOYEE_VERIFICATION_PHOTOS },
            }"
          >
            {{ t("employeeSettings.general.upgrade") }}
          </CoreLink>
        </template>
        <template #suffix>
          <CoreSaveConfirm :status="settingsSaveStatus.photo_capture_e" />
        </template>
      </CheckboxSetting>
      <CheckboxSetting
        :value="settings.employee_secure_mode"
        :disabled="employeeInOutDisabledForIpads"
        :disabled-message="
          t('employeeSettings.tooltips.enableIpadEmployeeInOut')
        "
        :loading="getCompanySettings.isLoading.value"
        :label="t('employeeSettings.general.employeeSectionPrivate')"
        @change="updateSetting('employee_secure_mode', Boolean($event))"
      >
        <i18n-t keypath="employeeSettings.general.employeeSectionPrivateDesc">
          <template #link>
            <CoreLink
              target="_blank"
              rel="noopener noreferrer"
              :href="t('urls.support.lockEmployeeList')"
              type="primary"
              :underline="false"
            >
              {{ t("employeeSettings.general.learnMoreLinkText") }}
            </CoreLink>
          </template>
        </i18n-t>
        <template #suffix>
          <CoreSaveConfirm :status="settingsSaveStatus.employee_secure_mode" />
        </template>
      </CheckboxSetting>
      <CheckboxSetting
        :value="settings.show_employee_in_out_timestamp"
        :disabled="employeeInOutDisabledForIpads"
        :disabled-message="
          t('employeeSettings.tooltips.enableIpadEmployeeInOut')
        "
        :loading="getCompanySettings.isLoading.value"
        :label="t('employeeSettings.general.displayTimestamp')"
        @change="
          updateSetting('show_employee_in_out_timestamp', Boolean($event))
        "
      >
        <template #suffix>
          <CoreSaveConfirm
            :status="settingsSaveStatus.show_employee_in_out_timestamp"
          />
        </template>
      </CheckboxSetting>
    </template>
  </SettingsTwoColumn>
</template>

<style lang="scss" scoped>
.section-title {
  margin-bottom: 30px;
}

.logout-selector-save-confirm {
  margin-left: 10px;
  font-size: 18px;
  vertical-align: middle;
}

.so-select {
  margin-top: 5px;
}
</style>
